import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useFlag } from '@labrav/flags'
import { CloudSelectorV2, SelectSize } from '@labrav/react-components'
import { SelectChangeEvent } from '@mui/material'

import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import { cloudTypes } from '../../utils/flyoutConstants'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import { useCurrentCloudProvider } from '../../utils/hooks/useCurrentCloudProvider'
import type { CloudMarketplace } from '../../types/marketplace'
import { setCloudMarketplaceProductListing } from '../../utils/hooks/useSyncCloudMarketplace'

export type CustomCloudOptions = {
  name: string
  disabled: boolean
}[]

interface CloudChangeV2Props {
  size?: SelectSize
  customCloudOptions?: CustomCloudOptions
  setInputValue?: React.Dispatch<React.SetStateAction<string>>
  setShowCrossMark?: React.Dispatch<React.SetStateAction<boolean>>
  setSearchNow?: React.Dispatch<React.SetStateAction<boolean>>
}

/** Matches marketplace `ProductsListingActionTypes.SET_SELECTED_PRODUCT`. */
const SET_SELECTED_PRODUCT = 'SET_SELECTED_PRODUCT'

const setSelectedProductOnListing = (productId?: string) => ({
  type: SET_SELECTED_PRODUCT,
  payload: productId,
})

type ProductsListingSlice = {
  products?: { rows?: { productId: string }[] }
  selectedProduct?: string
}

type CloudChangeRootState = {
  productsListing: Partial<Record<CloudMarketplace, ProductsListingSlice>>
}

export const CloudChangeV2: React.FC<CloudChangeV2Props> = ({
  size,
  customCloudOptions,
  setInputValue = () => {},
  setShowCrossMark = () => {},
  setSearchNow = () => {},
}) => {
  const { flag: isMicrosoftResaleAuthorizationsEnabled } = useFlag(
    'microsoftResellerAuthorization'
  )
  const cloudMarketplace = useCurrentCloudProvider({
    microsoftResellerAuthorization:
      isMicrosoftResaleAuthorizationsEnabled.value,
  })
  const dispatch = useDispatch()

  const products = useSelector(
    (state: CloudChangeRootState) =>
      state.productsListing[cloudMarketplace]?.products?.rows || []
  )
  const productId = useSelector(
    (state: CloudChangeRootState) =>
      state.productsListing[cloudMarketplace]?.selectedProduct
  )

  const { pathname } = useLocation()
  const isEnabled = !(
    pathname.includes('subscriptions') ||
    pathname.includes('resale-authorizations')
  )

  useEffect(() => {
    if (products.length > 0 && productId === undefined) {
      void dispatch(startLoading(LoadingTypes.GENERAL))
      void dispatch(
        actionTypeWrapper(
          cloudMarketplace,
          setSelectedProductOnListing(
            products.length === 1 ? products[0].productId : ''
          )
        )
      )
      void dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }, [products, productId, cloudMarketplace, dispatch])

  const _cloudTypes = cloudTypes
    .filter(cloud => cloud.name !== 'REDHAT')
    .map(cloud => {
      if ((cloud.name === 'AZURE' || cloud.name === 'GCP') && isEnabled) {
        return { ...cloud, disabled: false }
      } else {
        return cloud
      }
    })

  const handleCloudChange = useCallback(
    async (event: SelectChangeEvent) => {
      const selectedCloud = event.target.value as CloudMarketplace
      await dispatch(startLoading(LoadingTypes.GENERAL))
      await dispatch(
        actionTypeWrapper(
          cloudMarketplace,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(setCloudMarketplaceProductListing(selectedCloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)
    },
    [cloudMarketplace, setInputValue, setShowCrossMark, setSearchNow, dispatch]
  )

  return (
    <CloudSelectorV2
      cloudTypes={customCloudOptions || _cloudTypes}
      selectedCloud={cloudMarketplace}
      label="Cloud"
      onChange={handleCloudChange}
      minWidth={100}
      size={size || 'small'}
    />
  )
}
